import type { RadiusBlock, RadiusItem, RadiusRung, RadiusSentence } from './radiusTypes';
import { radiusRungs } from './radiusTypes';

/* Locating scored sentences inside the report's own prose.

   The API also returns each sentence's text, so the obvious move is to render
   straight from `sentences[]`. We deliberately don't: its splitter is lossy — on a
   verified run it dropped a closing quote that IS present in the source string — so
   rendering from it would silently rewrite report copy whenever the flag is on.
   Instead the original string stays the single source of truth and we only locate
   spans within it. A sentence we can't place is left unhighlighted; the text is
   never altered to force a match. */

export interface RadiusSegment {
  text: string;
  /** null for the prose between scored sentences, or anything we could not place. */
  sentence: RadiusSentence | null;
}

export interface RadiusAlignment {
  segments: RadiusSegment[];
  /** How many leading sentences were placed — lets a following element resume after them. */
  consumed: number;
}

interface Hit {
  index: number;
  length: number;
}

const TRAILING_PUNCTUATION = /[.!?…\s]+$/;
const REGEX_SPECIALS = /[.*+?^${}()|[\]\\]/g;

const escapeRegExp = (value: string): string => value.replace(REGEX_SPECIALS, '\\$&');

/** Match ignoring whitespace differences between the scored copy and the source. */
const locateLoosely = (source: string, text: string, from: number): Hit | null => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return null;
  const match = new RegExp(words.map(escapeRegExp).join('\\s+')).exec(source.slice(from));
  return match ? { index: from + match.index, length: match[0].length } : null;
};

const locate = (source: string, text: string, from: number): Hit | null => {
  if (!text) return null;

  const exact = source.indexOf(text, from);
  if (exact >= 0) return { index: exact, length: text.length };

  // Insight-card headlines come back with a period the rendered headline doesn't have.
  const trimmed = text.replace(TRAILING_PUNCTUATION, '');
  if (trimmed && trimmed !== text) {
    const found = source.indexOf(trimmed, from);
    if (found >= 0) return { index: found, length: trimmed.length };
  }

  return locateLoosely(source, text, from);
};

/**
 * Split `source` into segments, marking the parts covered by `sentences`.
 * Concatenating the segments always reproduces `source` exactly.
 */
export const alignSentences = (source: string, sentences?: RadiusSentence[]): RadiusAlignment => {
  if (!source) return { segments: [], consumed: 0 };

  const list = sentences ?? [];
  if (!list.length) return { segments: [{ text: source, sentence: null }], consumed: 0 };

  const segments: RadiusSegment[] = [];
  let cursor = 0;
  let consumed = 0;

  list.forEach((sentence, index) => {
    const hit = locate(source, sentence.text, cursor);
    if (!hit) return;

    if (hit.index > cursor) {
      segments.push({ text: source.slice(cursor, hit.index), sentence: null });
    }
    segments.push({ text: source.slice(hit.index, hit.index + hit.length), sentence });
    cursor = hit.index + hit.length;
    consumed = index + 1;
  });

  if (cursor < source.length) {
    segments.push({ text: source.slice(cursor), sentence: null });
  }

  return { segments, consumed };
};

/** Look up the item scoring a surface. Returns null rather than guessing at a near-miss. */
export const radiusItemFor = (block: RadiusBlock | undefined, path: string): RadiusItem | null => {
  const item = block?.items?.[path];
  return item && Array.isArray(item.sentences) ? item : null;
};

export interface RadiusMix {
  rungs: RadiusRung[];
  counts: number[];
  pct: number[];
  total: number;
}

/** The evidence mix from the API's own distribution — never recounted. `counts`/`pct` index `rungs`. */
export const radiusMix = (block?: RadiusBlock): RadiusMix => {
  const rungs = radiusRungs(block);
  const byTag = block?.distribution_by_tag;
  const byLevel = block?.distribution ?? {};
  // Tag-keyed first; reading only the numeric key makes the ledger vanish silently (ADR-0009).
  const counts = rungs.map((rung) => Number(byTag?.[rung.tag] ?? byLevel[String(rung.level)] ?? 0));
  const total = counts.reduce((sum, count) => sum + count, 0);
  return {
    rungs,
    counts,
    total,
    pct: counts.map((count) => (total ? (count / total) * 100 : 0)),
  };
};
